import React from 'react';
import { areEqual, type ListChildComponentProps } from 'react-window';
import CalendarSidebarCampaignRow from './calendar-sidebar-campaign-row';
import CalendarSidebarMessageRow from './calendar-sidebar-message-row';

export type SidebarItem = { _id: string; type: 'message' | 'campaign'; isGroup?: boolean; isUpcoming?: boolean };
export type SidebarRow =
    | { kind: 'divider'; label: string }
    | { kind: 'item'; item: SidebarItem };

export interface RowData {
    rows: SidebarRow[];
}

/**
 * One slot of the virtualised planner list.
 *
 * Memoised with react-window's own `areEqual`, which is the comparison the
 * library documents for exactly this: react-window re-renders its children on
 * EVERY scroll offset change, and a slot whose index, style and data did not
 * change has nothing new to draw. Unmemoised, every visible row — each a full
 * planner row with its avatars and a Radix menu tree, ~35 components — rendered
 * again on every scroll event: measured at ~10 rows and ~374 component renders
 * per scroll step on a 39-row list.
 */
const PlannerVirtualRowComponent = ({ index, style, data }: ListChildComponentProps<RowData>) => {
    const row = data.rows[index];

    if (!row) return null;

    if (row.kind === 'divider') {
        return (
            <div style={style}>
                <div className="flex items-center gap-2 px-3 h-[28px] text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted,#6b7280)]">
                    <span className="whitespace-nowrap">{row.label}</span>
                    <span className="flex-1 border-t border-[var(--color-border,#e5e7eb)]" />
                </div>
            </div>
        );
    }

    const item = row.item;

    return (
        <div style={style}>
            {item.type === 'message' ? (
                <CalendarSidebarMessageRow key={`message-${item._id}`} messageId={item._id} />
            ) : (
                <CalendarSidebarCampaignRow key={`campaign-${item._id}`} campaignId={item._id} />
            )}
        </div>
    );
};

export const PlannerVirtualRow = React.memo(PlannerVirtualRowComponent, areEqual);
